// HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09) -- data layer for Agenda + Tasks + the reschedule
// dependency, against the LIVE dev backend (net-new endpoints, no beta-contract equivalent). Per
// the brief (F:\AI_Data\projects\03-coowe-product\_planning\how-slots-backend-artifacts\
// FE-reimplement-teeup-webversion-brief.md): these are called DIRECTLY at /v1/... -- the shim
// (src/utils/devBackendShim.js) already passes any unmapped /v1 path through untouched and
// attaches ?token= on every request (see installDevBackendShim's request interceptor), so no new
// shim path-mapping is needed here. Guarded by isDevBackend() throughout (a no-op on prod/staging
// builds without the dev shim, same discipline as notificationsApi.js).
//
// RESILIENCE (plan section 4c, the hard acceptance criterion for this build): every READ here is
// fail-soft -- a network error, 404, 501, or "feature off" NEVER throws; it resolves to an empty
// value so a widget renders nothing instead of erroring the page. WRITEs (create/update/claim/
// delete) throw two TYPED errors the actions layer maps to specific UX:
//   - ConflictError  (HTTP 409 {"detail":"version_conflict"}) -- someone else edited first
//   - ClaimRaceError (HTTP 409 {"detail":"claim_race"})       -- someone else claimed the task first
// Any other write failure resolves to null (caller treats as "did not save", no throw to the page).
import api from "../api"
import { isDevBackend } from "@utils/devBackendShim"

export class ConflictError extends Error {
  constructor(message = "version_conflict") {
    super(message)
    this.name = "ConflictError"
  }
}
export class ClaimRaceError extends Error {
  constructor(message = "claim_race") {
    super(message)
    this.name = "ClaimRaceError"
  }
}

// api.ts's outer response interceptor rejects with `error.response` (an Axios-response-shaped
// object: {status, data, ...}), not the full AxiosError -- but be defensive to either shape (a
// network error with no response at all has neither `.status` nor `.response`).
const statusOf = e => e?.status ?? e?.response?.status
const detailOf = e => e?.data?.detail ?? e?.response?.data?.detail

const warn = (label, e) => {
  // eslint-disable-next-line no-console
  console.warn(`[agenda/tasks] ${label} failed`, e)
}

// Raises the typed error for a 409 write conflict; returns null (caller logs) for anything else.
const throwOnWriteConflict = (e, label) => {
  const status = statusOf(e)
  const detail = detailOf(e)
  if (status === 409 && detail === "claim_race") throw new ClaimRaceError()
  if (status === 409 && detail === "version_conflict") throw new ConflictError()
  warn(label, e)
  return null
}

// ---- capabilities (BE-7) -----------------------------------------------------------------
// GET /v1/capabilities -> {"agenda": bool, "tasks": bool}. Unreachable/erroring -> both off, so
// callers gate correctly without ever throwing into the page.
export const fetchCapabilities = async () => {
  if (!isDevBackend()) return { agenda: false, tasks: false }
  try {
    const { data } = await api.client.get("/v1/capabilities")
    return { agenda: !!data?.agenda, tasks: !!data?.tasks }
  } catch (e) {
    warn("fetchCapabilities", e)
    return { agenda: false, tasks: false }
  }
}

// ---- agenda (BE-2) -------------------------------------------------------------------------
export const listAgendaItems = async coId => {
  if (!isDevBackend() || !coId) return []
  try {
    const { data } = await api.client.get(
      `/v1/coordination-objects/${coId}/agenda-items`
    )
    const arr = Array.isArray(data) ? data : data?.items || []
    return arr.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  } catch (e) {
    warn("listAgendaItems", e)
    return []
  }
}

export const createAgendaItem = async (coId, fields) => {
  if (!isDevBackend() || !coId) return null
  try {
    const { data } = await api.client.post(
      `/v1/coordination-objects/${coId}/agenda-items`,
      fields
    )
    return data
  } catch (e) {
    warn("createAgendaItem", e)
    return null
  }
}

// fields carries only the changed columns; expectedVersion is the row's current `version`.
export const updateAgendaItem = async (itemId, fields, expectedVersion) => {
  if (!isDevBackend() || !itemId) return null
  try {
    const { data } = await api.client.patch(`/v1/agenda-items/${itemId}`, {
      ...fields,
      expected_version: expectedVersion,
    })
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "updateAgendaItem")
  }
}

// WU-P2-FE-a (2026-07-09) -- people-picker PATCH. Route is nested under the CO (unlike the plain
// item PATCH above). fields carries only the changed people columns (owner_participant_id and/or
// involved_people, explicit null/[] clears, an ABSENT key leaves it untouched); expectedVersion is
// REQUIRED by this endpoint's contract. owner_participant_id refs participants.id (the
// participant ROW id), not user_id -- see devBackendShim.js's devParticipantToBetaUser
// participantId field for the FE-side resolution of that id space.
export const updateAgendaItemPeople = async (coId, itemId, fields, expectedVersion) => {
  if (!coId || !itemId) return null
  try {
    const { data } = await api.client.patch(
      `/v1/coordination-objects/${coId}/agenda-items/${itemId}/people`,
      { ...fields, expected_version: expectedVersion }
    )
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "updateAgendaItemPeople")
  }
}

// itemIds = the FULL ordered list (one atomic transaction server-side, not N racing PATCHes).
export const reorderAgendaItems = async (coId, itemIds) => {
  if (!isDevBackend() || !coId) return false
  try {
    await api.client.put(
      `/v1/coordination-objects/${coId}/agenda-items/reorder`,
      { item_ids: itemIds }
    )
    return true
  } catch (e) {
    warn("reorderAgendaItems", e)
    return false
  }
}

export const deleteAgendaItem = async itemId => {
  if (!isDevBackend() || !itemId) return false
  try {
    await api.client.delete(`/v1/agenda-items/${itemId}`)
    return true
  } catch (e) {
    warn("deleteAgendaItem", e)
    return false
  }
}

// ---- tasks (BE-3a/3b) ----------------------------------------------------------------------
export const listTasks = async coId => {
  if (!isDevBackend() || !coId) return []
  try {
    const { data } = await api.client.get(
      `/v1/coordination-objects/${coId}/tasks`
    )
    return Array.isArray(data) ? data : data?.items || []
  } catch (e) {
    warn("listTasks", e)
    return []
  }
}

export const createTask = async (coId, fields) => {
  if (!isDevBackend() || !coId) return null
  try {
    const { data } = await api.client.post(
      `/v1/coordination-objects/${coId}/tasks`,
      fields
    )
    return data
  } catch (e) {
    warn("createTask", e)
    return null
  }
}

export const updateTask = async (taskId, fields, expectedVersion) => {
  if (!isDevBackend() || !taskId) return null
  try {
    const { data } = await api.client.patch(`/v1/tasks/${taskId}`, {
      ...fields,
      expected_version: expectedVersion,
    })
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "updateTask")
  }
}

// WU-P2-FE-a (2026-07-09) -- people-picker PATCH. assignee_id refs participants.user_id (the
// plain USER id) -- the OPPOSITE id space from the agenda people PATCH above (pre-existing
// backend schema asymmetry, respected as-is per the brief).
export const updateTaskPeople = async (taskId, fields, expectedVersion) => {
  if (!taskId) return null
  try {
    const { data } = await api.client.patch(`/v1/tasks/${taskId}/people`, {
      ...fields,
      expected_version: expectedVersion,
    })
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "updateTaskPeople")
  }
}

export const completeTask = (taskId, expectedVersion) =>
  updateTask(taskId, { completed_at: new Date().toISOString() }, expectedVersion)

export const uncompleteTask = (taskId, expectedVersion) =>
  updateTask(taskId, { completed_at: null }, expectedVersion)

// Claim is a RACE, not a version conflict -- conditional server-side UPDATE ... WHERE
// assignee_id IS NULL; 0 rows -> 409 {"detail":"claim_race"}.
export const claimTask = async taskId => {
  if (!isDevBackend() || !taskId) return null
  try {
    const { data } = await api.client.post(`/v1/tasks/${taskId}/claim`)
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "claimTask")
  }
}

export const deleteTask = async taskId => {
  if (!isDevBackend() || !taskId) return false
  try {
    await api.client.delete(`/v1/tasks/${taskId}`)
    return true
  } catch (e) {
    warn("deleteTask", e)
    return false
  }
}

// Organizer-only: POST /v1/coordination-objects/{coId}/reschedule-proposals with body
// {task_id: taskId}. 409 when no decided When; callers treat ConflictError as "not ready".
export const proposeReschedule = async (coId, taskId) => {
  if (!isDevBackend() || !coId || !taskId) return null
  try {
    const { data } = await api.client.post(
      `/v1/coordination-objects/${coId}/reschedule-proposals`,
      { task_id: taskId }
    )
    return data
  } catch (e) {
    return throwOnWriteConflict(e, "proposeReschedule")
  }
}

// Personal rollup (Home tasks card) -- assigned-to-or-created-by the caller, across every TeeUp
// they still participate in (BE-3b's EXISTS(participants) guard).
export const fetchMyTasks = async () => {
  if (!isDevBackend()) return []
  try {
    const { data } = await api.client.get("/v1/users/me/tasks")
    return Array.isArray(data) ? data : data?.items || []
  } catch (e) {
    warn("fetchMyTasks", e)
    return []
  }
}
